import React, { useState, useEffect, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AppBar, Toolbar, Typography, Switch, FormControlLabel, Paper, Box, Select, MenuItem, ClickAwayListener, IconButton, Modal } from '@mui/material';
import ProgressChart from './progressTile';
import axios from '../common/axios';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { styled } from '@mui/material/styles';
import TileTitle from '../common/tileTitle';
import ResourceChart from './resourceTile';
import OverallChart from './bulletTile';
import HeaderTitle from './headerTile';
import PartProgressChart from './partprogressTile';
import PartResourceChart from './partreesourceTile';
import PartPRDChart from './partproductivityTile';
import ProgressSummary from './progressSummary';
import MandaySummary from './mdSummary';
import RoomPRDChart from './roomProductivityTile';
import { DataGrid } from '@mui/x-data-grid';

import GridOnIcon from '@mui/icons-material/GridOn';
import _ from 'lodash';
import CircleIcon from '@mui/icons-material/Circle';

const ResponsiveGridLayout = WidthProvider(Responsive);

const theme = createTheme({
    palette: {
        primary: {
            main: '#2196f3',
        },
        secondary: {
            main: '#f50057',
        },
    },
});



const IOSSwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 32,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        transform: 'translateX(0px)',
        '&.Mui-checked': {
            color: '#fff',
            transform: 'translateX(28px)',
            '& .MuiSwitch-thumb:before': {
                backgroundImage: `url(${process.env.PUBLIC_URL}/images/switch.svg)`,

            },
            '& + .MuiSwitch-track': {
                opacity: 1,
                //   backgroundColor: '#aab4be',
                ...theme.applyStyles('dark', {
                    backgroundColor: '#8796A5',
                }),
            },
        },
        //   '&.Mui-disabled + .MuiSwitch-track': {
        //     opacity: 0.5,
        //   },

        '&.Mui-checked .MuiSwitch-thumb': {
            // color: '#2196f3',
            border: 'none',
            // backgroundColor:'#2196f3',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: "#F2F4F7",
            backgroundColor: '#F2F4F7',

            ...theme.applyStyles('dark', {
                color: theme.palette.grey[600],
            }),
        },
    },
    '& .MuiSwitch-thumb': {
        backgroundColor: 'transparent!important',
        width: 32,
        height: 32,
        boxShadow: 'none!important',
        '&::before': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/switch.svg)`,

        },

    },
    '& .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#F2F4F7',
        borderRadius: 34 / 2,
        ...theme.applyStyles('dark', {
            backgroundColor: '#8796A5',
        }),
    },
}));


const GridLayout = () => {
    const [isEditable, setIsEditable] = useState(false);
    const [projectList, setProjectList] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [criteriaDate, setCriteriaDate] = useState(dayjs().add(-1, 'days'));
    const [width, setWidth] = useState(1200);
    const [openDataGrid, setOpenDataGrid] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [rows, setRows] = useState([]);


    const fetchData = async (project, criteriaDate) => {
        try {
            const response = await axios.get(`/getprdData?project=${project}&cutoffdate=${criteriaDate}`);
            if (response.data.success) {
                return response.data.data;
            }
        } catch (error) {
            console.error('Error fetching level1 data:', error);
            // TODO: Provide appropriate feedback to the user
        }
        return [];
    };

    useEffect(() => {
        if (selectedProject && criteriaDate) {
            fetchData(selectedProject.projectnumber, criteriaDate).then(data => {
                setChartData(data);
            });
        }
    }, [selectedProject, criteriaDate]);

    const tableData =
        _(chartData && chartData.filter(v => v.monthweek === "W").map((v) => ({ ...v, uniq: v.level1 + "||" + v.level2 })))
            .groupBy('uniq')
            .map((objs, key) => ({
                level1: key.split("||")[0],
                level2: key.split("||")[1],
                'actual': (_.sumBy(objs, "actualQTY") / _.sumBy(objs, "EV_MD")) * 100,
                'target': (_.sumBy(objs, "planQTY") / _.sumBy(objs, "PV_MD")) * 100,

            }))
            .value();

    useEffect(() => {

        const groupedData = tableData.reduce((acc, item) => {
            if (!acc[item.level1]) {
                acc[item.level1] = [];
            }
            acc[item.level1].push(item);
            return acc;
        }, {});

        const rowsWithSpan = Object.entries(groupedData).flatMap(([level1, items]) => {
            return items.map((item, index) => ({
                id: `${level1}-${item.level2}`,
                level1,
                level1Span: index === 0 ? items.length : 0,
                ...item,
                signal: item.actual >= item.target ? 'green' : 'red',
            }));
        });

        setRows(rowsWithSpan);
    }, [tableData]);

    const columns = [
        {
            field: 'level1', headerName: '대공종', flex: 1,      headerAlign: 'center',
            align: 'center',renderCell: (params) => (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {params.value}
                </div>
            )
        },
        { field: 'level2', headerName: '협력업체', flex: 2,      headerAlign: 'center',
        align: 'center', },
        {
            field: 'actual', headerName: '현재 생산성', headerAlign: 'center',    align: 'center',
            rowSpanValueGetter: (value, row) => {
                return row ? `${row.level1}-${row.level2}` : value;
            },

            flex: 1, valueFormatter: (value, row) => !isNaN(value) ? value.toFixed(2) : "-"
        },
        {
            field: 'target', headerName: '목표 생산성',     headerAlign: 'center',
            align: 'center', flex: 1,
            rowSpanValueGetter: (value, row) => {
                return row ? `${row.level1}-${row.level2}` : value;
            },

            valueFormatter: (value, row) => value.toFixed(2)
        },
        {
            field: 'signal',
            headerName: '신호등',
            width: 100,
            headerAlign: 'center',

            rowSpanValueGetter: (value, row) => {
                return row ? `${row.actual}-${row.target}` : value;
            },
            renderCell: (params) => (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {!isNaN(params.row.actual) ? (
                        <CircleIcon color={params.value === 'green' ? 'success' : 'error'} />
                    ) : (
                        ''
                    )}
                </div>
            ),
        },
    ];


    const handleDataGridOpen = () => {
        setOpenDataGrid(true);
    };

    const handleDataGridClose = () => {
        setOpenDataGrid(false);
    };

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);



    useEffect(() => {
        const handleResize = () => {
            const padding = 40; // 좌우 패딩 합계 (예: 20px * 2)
            setWidth(window.innerWidth - padding);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // 초기 너비 설정

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);




    const [layout, setLayout] = useState([
        { i: 'title1', x: 0, y: 0, w: 2, h: 0.7, component: null, static: true },
        { i: 'title2', x: 2, y: 0, w: 1, h: 0.7, component: null, static: true },
        { i: 'a', x: 0, y: 0, w: 1, h: 1, component: null },
        { i: 'b', x: 1, y: 0, w: 1, h: 1, component: null },
        { i: 'c', x: 2, y: 0, w: 1, h: 2, component: null },
        { i: 'd', x: 0, y: 1, w: 1, h: 4, component: null },
        { i: 'e', x: 4, y: 0, w: 1, h: 3.5, component: null },
        { i: 'f', x: 0, y: 5, w: 1, h: 4, component: null },
        { i: 'g', x: 1, y: 5, w: 1, h: 4, component: null },
        { i: 'h', x: 1, y: 5, w: 1, h: 4, component: null },
        { i: 'i', x: 2, y: 6, w: 1, h: 3.5, component: null },
    ]);

    const handleLayoutChange = (newLayout) => {
        setLayout(newLayout.map(item => {

            if (item.i === 'a') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <ProgressSummary width={tileWidth} height={height} criteriaDate={criteriaDate} /> : null
                };
            } else if (item.i === 'b') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <MandaySummary width={tileWidth} height={height} criteriaDate={criteriaDate} /> : null
                };
            }

            else if (item.i === 'e') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <ProgressChart width={tileWidth} height={height} selectedProject={selectedProject.projectnumber} criteriaDate={criteriaDate} /> : null
                };
            } else if (item.i === 'f') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <PartPRDChart width={tileWidth} height={height} criteriaDate={criteriaDate} /> : null
                };
            }
            else if (item.i === 'd') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <PartProgressChart width={tileWidth} height={height} criteriaDate={criteriaDate} /> : null
                };
            } else if (item.i === 'g') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <PartResourceChart width={tileWidth} height={height} criteriaDate={criteriaDate} /> : null
                };
            } else if (item.i === 'h') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <RoomPRDChart width={tileWidth} height={height} selectedProject={selectedProject.projectnumber} criteriaDate={criteriaDate} /> : null
                };
            }



            else if (item.i === 'title2') {
                return {
                    ...item,
                    component: (
                        <Paper
                            elevation={3}
                            sx={{
                                borderRadius: "4px",
                                border: "1px solid #EFF0F6",
                                boxShadow: "0px 5px 20px 0px rgba(0, 0, 0, 0.05)",
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                transition: 'all 0.3s ease-in-out',
                                padding: '16px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <TileTitle sx={{ fontSize: 18, textAlign: 'center', margin: "0 auto" }}>Project Overall</TileTitle>
                        </Paper>
                    )
                };
            }
            else if (item.i === 'title1') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <HeaderTitle width={tileWidth} height={height} selectedProject={selectedProject.projectnumber} criteriaDate={criteriaDate} /> : null


                };
            } else if (item.i === 'i') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <ResourceChart width={tileWidth} height={height} selectedProject={selectedProject.projectnumber} criteriaDate={criteriaDate} /> : null
                };
            } else if (item.i === 'c') {
                const { w, h } = item;
                const tileWidth = w * width / 3;
                const height = h * 80;
                return {
                    ...item,
                    component: selectedProject ? <OverallChart width={tileWidth} height={height} selectedProject={selectedProject.projectnumber} criteriaDate={criteriaDate} /> : null
                };

            }
            return item;
        }));
    };

    const handleProjectChange = (event) => {
        setSelectedProject(projectList.find(v => v.projectname === event.target.value));
        setIsDropdownOpen(false);
    };

    const handleDropdownClose = () => {
        setIsDropdownOpen(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            const res = await axios.get(`/getprojectlistprd`);
            if (res.data.success) {
                setProjectList(res.data.data);
                setSelectedProject(res.data.data[0]);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        handleLayoutChange(layout);
    }, [selectedProject, criteriaDate]);



    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ flexGrow: 1, backgroundColor: "#F2F2F2" }}>
                <AppBar position="static" sx={{ backgroundColor: 'white', padding: "30px 20px", boxShadow: 'none' }}>
                    <Toolbar
                        sx={{
                            padding: '0px!important',
                            minHeight: '40px !important',
                            '@media (min-width:0px)': {
                                minHeight: '40px !important',
                            },
                            '@media (min-width:600px)': {
                                minHeight: '40px !important',
                            },
                        }}
                    >

                        <Box sx={{ position: 'relative', flexGrow: 1 }}>
                            {isDropdownOpen ? (
                                <Select
                                    size='small'
                                    open
                                    value={selectedProject.projectname}
                                    onChange={handleProjectChange}
                                    onClose={() => setIsDropdownOpen(false)} // onClose 이벤트 핸들러 추가

                                    sx={{
                                        position: 'absolute',
                                        top: -25,
                                        left: 0,
                                        zIndex: 1,
                                    }}
                                >
                                    {projectList.map((project) => (
                                        <MenuItem key={project.projectname} value={project.projectname}>
                                            {project.projectname}
                                        </MenuItem>
                                    ))}
                                </Select>
                            ) :
                                <ClickAwayListener onClickAway={handleDropdownClose}>
                                    <Typography
                                        variant="h6"
                                        component="span"
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        sx={{ cursor: 'pointer', color: "#262626", fontSize: 26, fontWeight: 600 }}
                                    >
                                        {selectedProject && selectedProject.projectname} 생산성 대시보드
                                    </Typography>
                                </ClickAwayListener>

                            }
                        </Box>

                        <IconButton onClick={handleDataGridOpen} color="gray" sx={{ marginRight: 1 }}>
                            <GridOnIcon />
                        </IconButton>

                        <FormControlLabel
                            control={<IOSSwitch title='스위치가 On되면 차트의 배열과 크기를 조정할 수 있습니다.' checked={isEditable} onChange={() => setIsEditable(!isEditable)} />}
                            // label="편집 모드"
                            sx={{ color: '#262626', marginLeft: 'auto' }}
                        />


                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                            <DatePicker
                                defaultValue={dayjs().add(-1, 'days')}
                                label="기준일"
                                value={criteriaDate}
                                onChange={(newValue) => setCriteriaDate(newValue)}
                                maxDate={dayjs().add(-1, 'days')}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            width: 160,
                                            '& .MuiInputBase-root': {
                                                height: '36px', // 원하는 높이로 설정
                                            },

                                            '& .MuiInputBase-input': {
                                                padding: '4px 8px', // 패딩 조정
                                                fontSize: '14px',   // 폰트 크기 조정
                                                color: '#262626',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: '#262626',
                                                fontSize: '20px', // 아이콘 크기 조정
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>

                    </Toolbar>
                </AppBar>
                <Box sx={{}}>
                    <ResponsiveGridLayout
                        className="layout"
                        layouts={{ lg: layout }}
                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                        cols={{ lg: 3, md: 3, sm: 1, xs: 1, xxs: 1 }}
                        margin={[20, 20]}
                        rowHeight={70}
                        width={width}
                        isDraggable={isEditable}
                        isResizable={isEditable}
                        onLayoutChange={handleLayoutChange}
                        draggableCancel=".no-drag-button"
                    >
                        {layout.map((item) => (
                            <Paper
                                key={item.i}
                                elevation={3}
                                sx={{
                                    borderRadius: "4px",
                                    border: "1px solid #EFF0F6",
                                    boxShadow: "0px 5px 20px 0px rgba(0, 0, 0, 0.05)",
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    // justifyContent: 'center',
                                    // alignItems: 'center',
                                    transition: 'all 0.3s ease-in-out',
                                }}
                            >
                                {item.component}
                            </Paper>
                        ))}
                    </ResponsiveGridLayout>
                </Box>

                <Modal open={openDataGrid} onClose={handleDataGridClose}>

                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 800, bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
                        
                        <Typography>업체별 생산성</Typography>
                        <DataGrid
                            rows={rows}
                            density="compact"
                            hideFooter
                            columns={columns}
                            getRowId={(row) => row.id}
                            rowHeight={50}
                            unstable_rowSpanning
                            showColumnVerticalBorder
                            showCellVerticalBorder
                            // getRowSpacing={(params) => ({
                            //     top: params.isFirstVisible ? 0 : 5,
                            //     bottom: params.isLastVisible ? 0 : 5,
                            // })}
                            sx={{
                                '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
                                    outline: 'none',
                                },
                            }}
                        />
                    </Box>
                </Modal>
            </Box>
        </ThemeProvider>
    );
};

export default GridLayout;